import { QueryClient } from '@tanstack/react-query';

import {
  apiCountryConfigurationNavigationRetrieve,
  getApiCountryConfigurationNavigationRetrieveQueryKey,
  useApiCountryConfigurationNavigationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
  useApiFrontendConfigurationRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { FrontendConfiguration } from '@rover/rsdk/src/apiClient/latest/model/frontendConfiguration';

// `BaseSeoLocationContainer` prefetches these for every engine, so a query added to the
// prefetch belongs in the hook too. Engine-specific queries belong in that engine's container.

export const prefetchSeoLocationSharedQueries = (queryClient: QueryClient): Promise<unknown> =>
  Promise.all([
    queryClient.prefetchQuery({
      queryKey: getApiCountryConfigurationNavigationRetrieveQueryKey(),
      queryFn: () => apiCountryConfigurationNavigationRetrieve(),
    }),
    queryClient.prefetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    }),
  ]);

export type SeoLocationSharedData = {
  countryConfig: CountryConfiguration | undefined;
  frontendConfig: FrontendConfiguration | undefined;
  isLoading: boolean;
  isError: boolean;
  // Which of the queries above is loading or failed, for the Sentry report.
  queryTags: Record<string, boolean>;
};

export const useSeoLocationSharedQueries = (): SeoLocationSharedData => {
  const {
    isLoading: isLoadingCountryConfig,
    isError: isErrorCountryConfig,
    data: countryConfig,
  } = useApiCountryConfigurationNavigationRetrieve();
  const {
    isLoading: isLoadingFrontendConfig,
    isError: isErrorFrontendConfig,
    data: frontendConfig,
  } = useApiFrontendConfigurationRetrieve();

  return {
    countryConfig,
    frontendConfig,
    isLoading: isLoadingCountryConfig || isLoadingFrontendConfig,
    isError: isErrorCountryConfig || isErrorFrontendConfig,
    queryTags: {
      isLoadingCountryConfig,
      isLoadingFrontendConfig,
      isErrorCountryConfig,
      isErrorFrontendConfig,
    },
  };
};
